import { Auth0DecodedHash, WebAuth } from 'auth0-js';
import config from '../config';
import history from '../history';

export class Auth {
  public auth0 = new WebAuth({
    domain: config.domain,
    clientID: config.clientId,
    redirectUri: config.callbackUrl,
    audience: `https://${config.domain}/api/v2/`,
    responseType: 'token id_token',
    scope: 'openid email',
  });

  constructor() {
    this.login = this.login.bind(this);
    this.logout = this.logout.bind(this);
    this.handleAuthentication = this.handleAuthentication.bind(this);
    this.isAuthenticated = this.isAuthenticated.bind(this);
  }

  public login() {
    this.auth0.authorize();
  }

  public handleAuthentication() {
    this.auth0.parseHash((err, authResult) => {
      if (authResult && authResult.accessToken && authResult.idToken) {
        this.setSession(authResult);
        if (this.isAdmin(authResult)) {
          history.replace('/admin');
        } else {
          history.replace('/quizResults');
        }
      } else if (
        err &&
        err.error === 'invalid_token' &&
        err.errorDescription &&
        err.errorDescription.includes('state')
      ) {
        this.handleAuthenticationFallback();
      } else if (err) {
        history.replace('/spendingTracker');
        alert(`Error: ${err.error}. Check the console for further details.`);
      } else {
        history.replace('/spendingTracker');
      }
    });
  }

  /*
  This function is needed because handleAuthentication() uses Auth0 direct communication required state parameters, which are absent when sending hash tokens from Ascend quizzes for new A/B test signup.
   */
  private handleAuthenticationFallback() {
    try {
      // Extract tokens from URL hash manually
      const hash = window.location.hash.substring(1); // Remove the #

      const params = new URLSearchParams(hash);
      const accessToken = params.get('access_token');
      const idToken = params.get('id_token');

      if (!accessToken || !idToken) {
        history.replace('/'); //go to login on bad route hit
        return;
      }

      // Validate tokens using Auth0
      this.validateTokens(accessToken, idToken);
    } catch (error) {
      history.replace('/'); //go to login on bad route hit
    }
  }

  private validateTokens(accessToken: string, idToken: string) {
    // Use Auth0's client to validate the tokens
    this.auth0.client.userInfo(accessToken, (err, profile) => {
      if (err) {
        history.replace('/'); //go to login on bad route hit
        return;
      }

      try {
        // Decode the ID token to get the payload
        const idTokenPayload = this.decodeJWT(idToken);

        // Create a mock authResult object similar to what parseHash would return
        const authResult: Auth0DecodedHash = {
          accessToken: accessToken,
          idToken: idToken,
          idTokenPayload: idTokenPayload,
          expiresIn: idTokenPayload.exp ? idTokenPayload.exp - Math.floor(Date.now() / 1000) : 3600,
          state: undefined, // We're bypassing state validation
        };

        // Continue with normal authentication flow
        this.setSession(authResult);

        // Force non-admin for fallback authentication
        history.replace('/quizResults');
      } catch (decodeError) {
        history.replace('/quizResults');
      }
    });
  }

  private decodeJWT(token: string): any {
    try {
      // Split the JWT into its parts
      const parts = token.split('.');
      if (parts.length !== 3) {
        throw new Error('Invalid JWT format');
      }

      // Decode the payload (second part)
      const payload = parts[1];

      // Add padding if needed (JWT base64 encoding doesn't include padding)
      const paddedPayload = payload + '='.repeat((4 - (payload.length % 4)) % 4);

      // Decode from base64
      const decodedPayload = atob(paddedPayload);

      // Return Parsed JSON Payload
      return JSON.parse(decodedPayload);
    } catch (error) {
      throw error;
    }
  }

  public setSession(authResult: Auth0DecodedHash) {
    const expiresAt = JSON.stringify(authResult.expiresIn! * 1000 + new Date().getTime());

    // TODO: Migrate from using localStorage to Redux
    localStorage.setItem('email', authResult.idTokenPayload.email);
    localStorage.setItem('isAdmin', String(this.isAdmin(authResult)));
    localStorage.setItem('access_token', authResult.accessToken!);
    localStorage.setItem('id_token', authResult.idToken!);
    localStorage.setItem('expires_at', expiresAt);

    //todo I  cant test this locally I think this may work on production we get the value confirmed at this point this way atleast
    // Identify user in Inspectlet for session tracking
    if ((window as any).__insp && authResult.idTokenPayload.email) {
      (window as any).__insp.push(['identify', authResult.idTokenPayload.email]);
    }

    history.replace('/spendingTracker');
  }

  public logout() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('id_token');
    localStorage.removeItem('expires_at');

    this.auth0.logout({ returnTo: config.baseUrl });
  }

  public isAuthenticated(): boolean {
    // Check whether the current time is past the
    // access token's expiry time
    const expiresAt = JSON.parse(localStorage.getItem('expires_at')!);
    return new Date().getTime() < expiresAt;
  }

  private isAdmin(result: Auth0DecodedHash) {
    const roleArr: string[] = result.idTokenPayload['https://tryascend.com/roles'];
    if (roleArr) {
      return roleArr.some((role) => role === 'admin');
    } else {
      return false;
    }
  }
}

export default new Auth();
